/**
 * Households list — landing page.
 *
 * Shows all households the user belongs to + a "create new" affordance.
 */

import { useForm } from "@tanstack/react-form";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useId, useMemo, useState } from "react";

import { t } from "../i18n";
import { cn } from "../lib/cn";
import { formatRelative } from "../lib/format";
import { createHouseholdSchema } from "../lib/schemas";
import { useFavoriteHouseholds } from "../lib/settings";
import { trpc } from "../lib/trpc";
import { fieldError } from "../lib/validation";
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardDescription,
  CardTitle,
  Dialog,
  EmptyState,
  Field,
  FieldMessage,
  highlightMatch,
  Icon,
  IconButton,
  Input,
  PageHeader,
  SkeletonGate,
  Stack,
  useToast,
} from "./ui";

export default function HouseholdsList() {
  const navigate = useNavigate();
  const households = trpc.household.list.useQuery();
  const invitations = trpc.invitation.listMine.useQuery();
  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { isFavorite, toggleFavorite } = useFavoriteHouseholds();

  const onSelect = (householdId: string) =>
    navigate({
      to: "/households/$householdId/$tab",
      params: { householdId, tab: "settlements" },
    });

  const filteredInvitations = useMemo(() => {
    if (!invitations.data) return [];
    const q = search.trim().toLocaleLowerCase("nb-NO");
    if (!q) return invitations.data;
    return invitations.data.filter((inv) =>
      inv.householdName.toLocaleLowerCase("nb-NO").includes(q),
    );
  }, [invitations.data, search]);

  const filteredHouseholds = useMemo(() => {
    if (!households.data) return [];
    const q = search.trim().toLocaleLowerCase("nb-NO");
    const list = q
      ? households.data.filter((h) => h.name.toLocaleLowerCase("nb-NO").includes(q))
      : households.data;
    // Stable sort: favoritter forst, behold opprinnelig rekkefolge ellers.
    return [...list].sort((a, b) => {
      const aFav = isFavorite(a.id);
      const bFav = isFavorite(b.id);
      if (aFav && !bFav) return -1;
      if (!aFav && bFav) return 1;
      return 0;
    });
  }, [households.data, search, isFavorite]);

  const totalCount = (invitations.data?.length ?? 0) + (households.data?.length ?? 0);
  const filteredCount = filteredInvitations.length + filteredHouseholds.length;
  // Sok-felt vises kun nar det er noe a soke i — unngar tomt felt pa onboarding.
  const showSearch = totalCount > 1;
  const isFiltered = search.trim().length > 0;

  return (
    <>
      <PageHeader
        icon="home"
        title={t.households.title}
        description={t.households.description}
        actions={
          <Button variant="primary" iconLeft="plus" onClick={() => setCreateOpen(true)}>
            {t.households.newHousehold}
          </Button>
        }
      />

      {showSearch && (
        <div className="mb-4">
          <Input
            iconLeft="search"
            placeholder={t.households.searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label={t.households.searchLabel}
            className="h-11 sm:h-9"
          />
        </div>
      )}

      <SkeletonGate
        loading={households.isPending}
        shape="cards"
        count={4}
        cardColumns={2}
        label={t.common.loading}
      >
        {households.isError && (
          <Card padding="lg">
            <div className="text-center text-rose-700 text-sm dark:text-rose-300">
              {t.households.loadFailed(households.error.message)}
            </div>
          </Card>
        )}

        {households.data?.length === 0 && (invitations.data?.length ?? 0) === 0 && (
          <Card padding="none">
            <EmptyState
              icon="home"
              title={t.households.emptyTitle}
              description={t.households.emptyDescription}
              action={
                <Button variant="primary" iconLeft="plus" onClick={() => setCreateOpen(true)}>
                  {t.households.emptyAction}
                </Button>
              }
            />
          </Card>
        )}

        {isFiltered && filteredCount === 0 && totalCount > 0 && (
          <Card padding="lg">
            <p className="text-center text-ink-500 text-sm">{t.households.searchEmpty(search)}</p>
          </Card>
        )}

        {filteredCount > 0 && (
          <div className="grid gap-3 sm:grid-cols-2">
            {filteredInvitations.map((inv) => (
              <InvitationCard key={inv.id} invitation={inv} onAccepted={onSelect} search={search} />
            ))}
            {filteredHouseholds.map((h) => {
              const fav = isFavorite(h.id);
              return (
                <Card key={h.id} interactive onClick={() => onSelect(h.id)} padding="md">
                  <Stack gap={2}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 flex-col gap-2">
                        <div>
                          <CardTitle className="truncate">
                            {highlightMatch(h.name, search)}
                          </CardTitle>
                          <CardDescription>
                            {t.households.createdAt(formatRelative(h.createdAt))}
                          </CardDescription>
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5">
                          {fav && (
                            <Badge
                              tone="warning"
                              iconLeft="star"
                              className="border border-amber-400 dark:border-amber-500 [&_svg]:fill-amber-500 dark:[&_svg]:fill-amber-400"
                            >
                              {t.households.favoriteBadge}
                            </Badge>
                          )}
                          {h.role === "owner" ? (
                            <Badge tone="accent" variant="solid" iconLeft="crown">
                              {t.roles.owner}
                            </Badge>
                          ) : (
                            <Badge tone="neutral" variant="outline" iconLeft="user">
                              {t.roles.member}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <IconButton
                        icon="star"
                        size="sm"
                        variant="soft"
                        label={
                          fav ? t.households.favoriteRemoveLabel : t.households.favoriteAddLabel
                        }
                        aria-pressed={fav}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(h.id);
                        }}
                        className={cn(
                          "shrink-0 border transition-colors",
                          fav
                            ? "border-amber-400 bg-amber-50 text-amber-600 hover:bg-amber-100 dark:border-amber-500 dark:bg-amber-900/30 dark:text-amber-300 dark:hover:bg-amber-900/40 [&_svg]:fill-amber-400 dark:[&_svg]:fill-amber-400"
                            : "border-ink-200 bg-transparent text-ink-500 hover:bg-ink-100 hover:text-ink-800 dark:border-ink-300",
                        )}
                      />
                    </div>
                    <div className="flex items-center gap-3 pt-1 text-ink-500 text-xs">
                      <span className="inline-flex items-center gap-1">
                        <Icon name="users" size="xs" />
                        {t.households.memberCount(h.memberCount)}
                      </span>
                      {h.activeSettlementCount > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <Icon name="receipt" size="xs" />
                          {t.households.activeSettlements(h.activeSettlementCount)}
                        </span>
                      )}
                    </div>
                  </Stack>
                </Card>
              );
            })}
          </div>
        )}
      </SkeletonGate>

      <CreateHouseholdDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => {
          setCreateOpen(false);
          onSelect(id);
        }}
      />
    </>
  );
}

function CreateHouseholdDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const utils = trpc.useUtils();
  const create = trpc.household.create.useMutation({
    onSuccess: (data) => {
      utils.household.list.invalidate();
      onCreated(data.id);
    },
  });

  const form = useForm({
    defaultValues: { name: "" },
    validators: {
      onChange: createHouseholdSchema,
      onSubmit: createHouseholdSchema,
    },
    onSubmit: ({ value }) => create.mutate({ name: value.name.trim() }),
  });

  useEffect(() => {
    if (open) form.reset({ name: "" });
  }, [open, form]);

  const formId = useId();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t.households.createDialog.title}
      description={t.households.createDialog.description}
      footer={
        <>
          <Button onClick={onClose}>{t.common.cancel}</Button>
          <Button type="submit" form={formId} variant="primary" isLoading={create.isPending}>
            {t.common.create}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <form.Field name="name">
          {(field) => (
            <Field label={t.households.createDialog.nameLabel} required error={fieldError(field)}>
              <Input
                autoFocus
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                placeholder={t.households.createDialog.namePlaceholder}
                invalid={!!fieldError(field)}
              />
            </Field>
          )}
        </form.Field>
        {create.isError && (
          <FieldMessage tone="error" className="mt-2 text-sm">
            {create.error.message}
          </FieldMessage>
        )}
      </form>
    </Dialog>
  );
}

type InvitationListItem = {
  id: string;
  householdName: string;
  invitedByDisplayName: string | null;
};

/**
 * Invitasjons-kort som rendres inline med husholdning-kortene i grid'et.
 * Har samme storrelse som husholdning-kort, men med en aksent-border + badge
 * for a vise at det er en ventende invitasjon — pluss aksepter/avsla-knapper.
 */
function InvitationCard({
  invitation,
  onAccepted,
  search,
}: {
  invitation: InvitationListItem;
  onAccepted: (householdId: string) => void;
  search: string;
}) {
  const utils = trpc.useUtils();
  const { show } = useToast();
  const [unverifiedOpen, setUnverifiedOpen] = useState(false);

  const accept = trpc.invitation.accept.useMutation({
    onSuccess: (data) => {
      utils.invitation.listMine.invalidate();
      utils.household.list.invalidate();
      show({
        tone: "success",
        title: t.invitations.list.acceptedToast,
        dismissLabel: t.invitations.toast.dismissLabel,
      });
      onAccepted(data.householdId);
    },
    onError: (error) => {
      // requireVerifiedEmail returnerer FORBIDDEN nar email_verified_at er
      // NULL — egen dialog er bedre enn generisk toast siden bruker bare
      // trenger a klikke verifiseringslenken.
      if (error.data?.code === "FORBIDDEN") {
        setUnverifiedOpen(true);
        return;
      }
      show({
        tone: "danger",
        title: error.message,
        dismissLabel: t.invitations.toast.dismissLabel,
      });
    },
  });

  const decline = trpc.invitation.decline.useMutation({
    onSuccess: () => {
      utils.invitation.listMine.invalidate();
      show({
        tone: "info",
        title: t.invitations.list.declinedToast,
        dismissLabel: t.invitations.toast.dismissLabel,
      });
    },
  });

  const busy = accept.isPending || decline.isPending;

  return (
    <>
      <Card padding="md" className="border-accent-300 dark:border-accent-700">
        <Stack gap={2}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <Avatar name={invitation.householdName} />
              <div className="min-w-0">
                <CardTitle className="truncate">
                  {highlightMatch(invitation.householdName, search)}
                </CardTitle>
                <CardDescription className="truncate">
                  {invitation.invitedByDisplayName
                    ? t.invitations.list.invitedBy(invitation.invitedByDisplayName)
                    : t.invitations.list.invitedAnonymous}
                </CardDescription>
              </div>
            </div>
            <Badge tone="accent" iconLeft="mail">
              {t.invitations.list.pendingBadge}
            </Badge>
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button
              variant="primary"
              size="sm"
              onClick={() => accept.mutate({ invitationId: invitation.id })}
              isLoading={accept.isPending}
              disabled={busy}
            >
              {t.invitations.list.accept}
            </Button>
            <Button
              size="sm"
              onClick={() => decline.mutate({ invitationId: invitation.id })}
              isLoading={decline.isPending}
              disabled={busy}
            >
              {t.invitations.list.decline}
            </Button>
          </div>
        </Stack>
      </Card>
      <UnverifiedEmailDialog open={unverifiedOpen} onClose={() => setUnverifiedOpen(false)} />
    </>
  );
}

function UnverifiedEmailDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { show } = useToast();
  const me = trpc.auth.me.useQuery();
  const resend = trpc.auth.resendVerification.useMutation({
    onSuccess: () => {
      show({
        tone: "success",
        title: t.invitations.list.unverifiedDialog.resentToast,
        dismissLabel: t.invitations.toast.dismissLabel,
      });
      onClose();
    },
    onError: (error) => {
      show({
        tone: "danger",
        title: t.invitations.list.unverifiedDialog.resendFailedToast,
        description: error.message,
        dismissLabel: t.invitations.toast.dismissLabel,
      });
    },
  });
  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="sm"
      title={t.invitations.list.unverifiedDialog.title}
      description={t.invitations.list.unverifiedDialog.description}
      footer={
        <>
          <Button onClick={onClose} disabled={resend.isPending}>
            {t.invitations.list.unverifiedDialog.closeButton}
          </Button>
          <Button variant="primary" isLoading={resend.isPending} onClick={() => resend.mutate()}>
            {t.invitations.list.unverifiedDialog.resendButton}
          </Button>
        </>
      }
    >
      {me.data?.email && (
        <p className="text-ink-600 text-sm">
          {t.invitations.list.unverifiedDialog.emailLine(me.data.email)}
        </p>
      )}
    </Dialog>
  );
}
